import type { ReactNode } from 'react';
import { RefreshCw, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Skeleton } from '@/components/atoms/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/atoms/tooltip';
import { cn } from '@/lib/utils';
import { BrandLogo, type BrandLogoId } from './BrandLogo';
import {
  HEALTH_DOT_CLASSES,
  HEALTH_TEXT_CLASSES,
  type IntegrationHealth,
} from './integration-tokens';

/**
 * One card in the Connected Apps directory.
 *
 * The whole card is a fixed four-band stack — brand, blurb, facts, actions —
 * and the facts band is the only one that flexes. That's what makes a grid of
 * eight unrelated services read as one system: every logo sits on the same
 * baseline, every status sits in the same corner, and every footer lines up
 * across a row no matter how much detail the middle carries.
 *
 * Anything that would blow the card out of that shape (Wisetack's enrollment
 * form, Stripe's requirements list) belongs in a dialog or its own panel, not
 * in here.
 */
export interface IntegrationCardProps {
  /** Anchor id, also the test id suffix, so the banner can deep-link here. */
  anchorId: string;
  name: string;
  /** Small category label under the name — hierarchy without section headers. */
  category: string;
  logo: BrandLogoId;
  description: string;
  health: IntegrationHealth;
  statusLabel: string;
  /** Extra chip beside the status (e.g. Payabli TEST/LIVE). */
  statusAdornment?: ReactNode;
  isLoading?: boolean;
  error?: { message: string; onRetry?: () => void } | null;
  /** Two or three key/value facts. Kept short on purpose. */
  facts?: Array<{ label: string; value: ReactNode }>;
  /**
   * Richer content that replaces the facts list — for cards whose connected
   * state can't be squeezed into a flat key/value list (e.g. Zapier's one row
   * per bindable connection). Takes precedence over `facts` when provided.
   */
  body?: ReactNode;
  actions?: ReactNode;
}

export function IntegrationCard({
  anchorId,
  name,
  category,
  logo,
  description,
  health,
  statusLabel,
  statusAdornment,
  isLoading = false,
  error,
  facts,
  body,
  actions,
}: IntegrationCardProps) {
  return (
    <article
      id={anchorId}
      data-testid={`integration-card-${anchorId}`}
      data-health={health}
      className={cn(
        // One hairline only. A `ring-1` sits at the border-box edge and a
        // tight `0 1px 2px` shadow lands immediately under it, so the two
        // together read as a doubled line around every card. `border` +
        // `shadow-sm` is the same token DetailCard uses, so these sit
        // consistently with every other card surface in the product.
        'group flex h-full scroll-mt-6 flex-col overflow-hidden rounded-xl border bg-card text-card-foreground shadow-sm',
        // The lift is what keeps it feeling premium without a second edge.
        'transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md',
      )}
    >
      {/* items-center, not items-start: the name + category block is two lines
          tall, so a top-aligned 32px mark sits visibly high of its own title.
          Centring puts the logo on the optical midline of the pair. */}
      <div className="flex items-center gap-3.5 px-5 pb-4 pt-5">
        <BrandLogo id={logo} />
        {/* Type scale is lifted straight from the settings sidebar so the card
            and the nav it sits beside read as one system: nav items are
            `text-sm font-medium`, section labels are `text-[11px]
            font-semibold uppercase tracking-[0.06em]`. */}
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-sm font-semibold leading-5">{name}</h3>
          <p className="truncate text-[11px] font-semibold uppercase leading-4 tracking-[0.06em] text-muted-foreground/70">
            {category}
          </p>
        </div>
        <div className="shrink-0">
          {isLoading ? (
            <Skeleton className="h-4 w-20" />
          ) : (
            <div className="flex items-center gap-2">
              <IntegrationStatus health={health} label={statusLabel} />
              {statusAdornment}
            </div>
          )}
        </div>
      </div>

      {/* The card only has room for one `text-sm`, and the name has earned it.
          Blurb and facts sit a step down at 13px so the eye lands on the app
          and its status first — at a flat 14px throughout, every card read as
          an undifferentiated block of text. */}
      <div className="flex-1 px-5 pb-5">
        <p className="text-[13px] leading-5 text-muted-foreground">{description}</p>

        {isLoading ? (
          <div className="mt-3.5 space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-4/5" />
          </div>
        ) : error ? (
          <div className="mt-3.5 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-[13px] text-destructive">
            <TriangleAlert className="size-3.5 shrink-0" aria-hidden />
            <p className="min-w-0 flex-1 leading-5">{error.message}</p>
            {error.onRetry ? (
              <Button type="button" variant="outline" size="sm" onClick={error.onRetry}>
                <RefreshCw className="size-3.5" />
                Retry
              </Button>
            ) : null}
          </div>
        ) : body ? (
          body
        ) : facts && facts.length > 0 ? (
          <dl className="mt-3.5 space-y-2 border-t border-border/60 pt-3.5">
            {facts.map((fact) => (
              <div
                key={fact.label}
                className="flex items-baseline justify-between gap-4 text-[13px]"
              >
                <dt className="shrink-0 text-muted-foreground">{fact.label}</dt>
                <dd className="min-w-0 truncate text-right font-medium leading-5 tabular-nums text-foreground">
                  {fact.value ?? '—'}
                </dd>
              </div>
            ))}
          </dl>
        ) : null}
      </div>

      {actions ? (
        <div className="flex flex-wrap items-center gap-2 border-t border-border/60 bg-muted/30 px-5 py-3.5">
          {actions}
        </div>
      ) : null}
    </article>
  );
}

/**
 * Status is a coloured dot plus near-neutral text. The logos already carry the
 * colour in this layout — filled status pills on top of them turn every card
 * into a swatch board.
 */
export function IntegrationStatus({ health, label }: { health: IntegrationHealth; label: string }) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap text-xs font-medium leading-5',
        HEALTH_TEXT_CLASSES[health],
      )}
    >
      <span className={cn('size-1.5 rounded-full', HEALTH_DOT_CLASSES[health])} aria-hidden />
      {label}
    </span>
  );
}

/**
 * Write actions on this page are guarded by `company:edit` on the backend.
 * Rather than hide them from viewers — which makes the capability invisible —
 * we render them disabled and explain why on hover/focus. Radix needs a real
 * (non-disabled) element to attach the trigger to, hence the wrapping span.
 */
export function PermissionHint({
  show,
  reason = 'You need the Company edit permission to change this.',
  children,
}: {
  show: boolean;
  reason?: string;
  children: ReactNode;
}) {
  if (!show) return <>{children}</>;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex" tabIndex={0}>
            {children}
          </span>
        </TooltipTrigger>
        <TooltipContent>{reason}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** Pushes whatever follows it to the right edge of the card footer. */
export function FooterSpacer() {
  return <span className="flex-1" aria-hidden />;
}
